import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * ScrollToTop component that automatically scrolls to the top of the page
 * when the route changes. This ensures users always start at the top of a new page.
 *
 * Note: This only triggers on pathname changes, not hash changes,
 * so anchor links within the same page will still work properly.
 */
export const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    // If there's a hash (anchor link), let the browser handle it
    if (hash && hash.length > 1) {
      // Small delay to ensure the page content is loaded
      setTimeout(() => {
        // OAuth callbacks (and other flows) can include non-selector hash fragments
        // like `#error=...`, which would crash `querySelector`. Use `getElementById`
        // to avoid selector parsing.
        try {
          const id = decodeURIComponent(hash.slice(1))
          const element = document.getElementById(id)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        } catch {
          // Ignore invalid hash fragments
        }
      }, 100)
    } else {
      // No hash, scroll to top
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant', // Instant scroll for better UX on page changes
      })
    }
  }, [pathname, hash])

  return null // This component doesn't render anything
}

export default ScrollToTop
